import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { markPortalNotificationReadFromAction } from '../services/notifications';
import {
  applyReadToNotificationsCache,
  countUnreadChannelNotificationsInCache,
  getPortalNotificationsQueryKey,
  invalidatePortalNotificationsList,
} from './portalNotificationsCache';
import { preparePortalNotificationRead } from '../utils/notificationReadActions';

export {
  applyMarkAllReadToNotificationsCache,
  invalidatePortalNotificationsList,
} from './portalNotificationsCache';

export const useMarkNotificationReadMutation = () => {
  const queryClient = useQueryClient();
  const { userRole, decrementNotificationCount, incrementNotificationCount } = useAuth();

  return useMutation({
    mutationFn: async (source) => {
      const prepared = preparePortalNotificationRead(source);
      if (!prepared) {
        throw new Error('Notification id or channel id is required');
      }

      const result = await markPortalNotificationReadFromAction(prepared.action);
      if (!result.success) {
        throw new Error(result.message || 'Failed to mark notification as read');
      }

      return { ...result, action: prepared.action };
    },
    onMutate: async (source) => {
      const prepared = preparePortalNotificationRead(source);
      if (!prepared) {
        return null;
      }

      const queryKey = getPortalNotificationsQueryKey(userRole);
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData(queryKey);
      const badgeDelta =
        prepared.action.mode === 'channel'
          ? countUnreadChannelNotificationsInCache(
              queryClient,
              userRole,
              prepared.action.channelId,
            ) || 1
          : 1;

      applyReadToNotificationsCache(queryClient, userRole, prepared.action);
      decrementNotificationCount(badgeDelta);

      return { previous, badgeDelta };
    },
    onError: (_error, _source, context) => {
      if (context?.previous) {
        queryClient.setQueryData(getPortalNotificationsQueryKey(userRole), context.previous);
      }
      if (context?.badgeDelta) {
        incrementNotificationCount(context.badgeDelta);
      }
      invalidatePortalNotificationsList(queryClient, userRole);
    },
  });
};
